import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Eye, Link2, Loader2, MessageCircle, ShieldCheck, Trash2, Upload } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, Bi } from "@/lib/i18n";
import { dateLabel, waLink } from "@/lib/format";
import { Card } from "@/components/common";
import { ID_LABEL } from "@/components/AddTenantSheet";

export function ShareLinkCard({ tenant }: { tenant: any }) {
  const { t } = useT();
  const { property } = useAuth();
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenant.id}/share-link`);
      setLink(`${window.location.origin}/t/${r.data.token}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    await api.post(`/tenants/${tenant.id}/share-link/revoke`);
    setLink(null);
    toast.success("Old links disabled");
  };
  const msg = link ? `Hi ${tenant.name}, here is your ${property?.name} page with rent status, receipts and today's menu (no login needed): ${link}` : "";
  return (
    <Card className="p-4" data-testid="share-link-card">
      <p className="font-bold flex items-center gap-2"><Link2 className="h-4 w-4 text-indigo-600" /><Bi k="share_link" /></p>
      <p className="text-xs text-slate-500 mt-1">{t("share_link_help")}</p>
      {!link ? (
        <Button data-testid="share-link-generate-btn" disabled={busy} onClick={make} variant="outline" className="mt-3 w-full h-12 rounded-xl">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("share_link")}
        </Button>
      ) : (
        <div className="mt-3 space-y-2">
          <p data-testid="share-link-url" className="text-xs break-all rounded-lg bg-slate-50 border border-slate-200 p-2 font-mono-num">{link}</p>
          <div className="grid grid-cols-2 gap-2">
            <a data-testid="share-link-whatsapp-btn" href={waLink(tenant.phone, msg)} target="_blank" rel="noreferrer" className="h-12 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-1.5 pg-tap">
              <MessageCircle className="h-4 w-4" />{t("send_whatsapp")}
            </a>
            <Button data-testid="share-link-copy-btn" variant="outline" className="h-12 rounded-xl" onClick={() => { navigator.clipboard?.writeText(link); toast.success(t("link_copied")); }}>
              <Copy className="h-4 w-4 mr-1" />{t("copy_link")}
            </Button>
          </div>
        </div>
      )}
      <button data-testid="share-link-revoke-btn" onClick={revoke} className="mt-3 text-xs font-semibold text-slate-500 underline underline-offset-2">{t("revoke_link")}</button>
    </Card>
  );
}

function IdDoc({ tenant, onChanged }: { tenant: any; onChanged: () => void }) {
  const { t } = useT();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tenants/${tenant.id}/id-doc`, fd);
      toast.success("Uploaded");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const view = async () => {
    const win = window.open("", "_blank");
    try {
      const r = await api.post(`/tenants/${tenant.id}/id-doc/view-link`);
      const url = `${API.replace(/\/api$/, "")}${r.data.url}`;
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      win?.close();
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete ID image?")) return;
    await api.delete(`/tenants/${tenant.id}/id-doc`);
    onChanged();
  };
  return (
    <div className="mt-3 rounded-xl border border-slate-200 p-3">
      <p className="text-sm font-semibold">{t("id_doc")}</p>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="id-doc-file-input" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      {tenant.id_doc ? (
        <div className="flex gap-2 mt-2">
          <Button data-testid="id-doc-view-btn" variant="outline" className="h-11 rounded-xl flex-1" onClick={view}><Eye className="h-4 w-4 mr-1" />{t("view_id")}</Button>
          <Button data-testid="id-doc-delete-btn" variant="outline" className="h-11 rounded-xl text-rose-600" onClick={remove}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ) : (
        <Button data-testid="id-doc-upload-btn" disabled={busy || !tenant.consent?.given} variant="outline" className="mt-2 h-11 rounded-xl w-full" onClick={() => ref.current?.click()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />{t("upload_id")}</>}
        </Button>
      )}
    </div>
  );
}

export function IdCard({ tenant, onChanged }: { tenant: any; onChanged: () => void }) {
  const { t, lang } = useT();
  const { property } = useAuth();
  return (
    <Card className="p-4" data-testid="tenant-id-card">
      <p className="font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-700" /><Bi k="id_details" /></p>
      <p className="text-sm mt-2" data-testid="tenant-id-summary">
        {tenant.id_type && tenant.id_type !== "none" ? <>{ID_LABEL[tenant.id_type]} · XXXX-{tenant.id_last4}</> : t("id_none")}
      </p>
      {tenant.consent?.given && <p className="text-xs text-slate-500">{t("consent_at")}: {dateLabel(tenant.consent.at, lang)}</p>}
      {property?.id_upload_enabled && tenant.status === "active" ? <IdDoc tenant={tenant} onChanged={onChanged} /> : (
        <p className="text-xs text-slate-500 mt-2">{t("id_upload_off")}</p>
      )}
    </Card>
  );
}
